import { AutorenewRetailPriceComponentModule } from '@b2b/autorenew/components/autorenew-retail-price';
import { IconComponentModule } from '@b2b/common/components/icon';
import { VCardDirectiveModule } from '@b2b/common/directives/vcard';
import { AddressService, AddressServiceModule } from '@b2b/common/services/address';
import { CartMembershipsService, CartMembershipsServiceModule } from '@b2b/common/services/cart/memberships';
import { output, TODO } from '@b2b/types/core';
import { AutorenewalType, Membership, MembershipCardholder } from '@b2b/types/membership';
import angular from 'angular';

class CartMembershipController {
  static $inject = ['AddressService', 'MembershipService', 'CartMembershipsService', 'ValidateService'];
  showAutorenewDisclaimer = false;
  currentOffering: { autorenewalType: AutorenewalType } | null = null;
  membershipLevelDetail!: TODO;
  membership!: Membership;
  membershipErrorMessages: Record<string, TODO> = {};
  customerDuplicates: TODO[] = [];
  customerMemberships: TODO[] = [];
  showCustomers = false;
  showCardModal = output<{ cardholder: MembershipCardholder | null; membership: Membership; mode: string }>();
  showRemoveCardModal = output<{ cardholder: MembershipCardholder }>();
  showReviewPotentialMatchesModal = output<TODO>();
  restoreCardholder = output<{ cardholder: MembershipCardholder }>();
  autorenewChanged = output<void>();

  constructor(
    private addressService: AddressService,
    private MembershipService: TODO,
    private cartMembershipsService: CartMembershipsService,
    private ValidateService: TODO,
  ) {}

  $onChanges() {
    this.currentOffering = this.cartMembershipsService.findSelectedOfferingFromMembershipLevels(
      this.membershipLevelDetail ? [this.membershipLevelDetail] : [],
      this.membership,
    );

    this.showAutorenewDisclaimer = this.cartMembershipsService.isAutorenewType({
      autorenewalType: this.currentOffering ? this.currentOffering.autorenewalType : undefined,
    });
  }

  isCardType(cardholder: MembershipCardholder, type: MembershipCardholder['cardType']) {
    return cardholder && cardholder.cardType && cardholder.cardType === type;
  }

  isMembershipRenewal(membership) {
    return this.MembershipService.isRenewal(membership);
  }

  isMembershipUpgradeDowngrade(membership) {
    return this.MembershipService.isUpgradeDowngrade(membership);
  }

  canAddCardholders(membership) {
    let cardholders = [];
    let maxNumCardholders = 0;

    if (membership.level) {
      maxNumCardholders = membership.level.rules.numberOfCardHolders;
    }

    if (Array.isArray(membership.membershipInfo.membershipCards)) {
      cardholders = membership.membershipInfo.membershipCards.filter((card) => {
        return !card.excludeCardOnMemberCreation;
      });
    }

    return cardholders.length < maxNumCardholders;
  }

  addCardholder(membership) {
    this.showCardModal({ $event: { cardholder: null, membership, mode: 'create' } });
  }

  checkCardholderHasErrors(cardholder: MembershipCardholder, fieldName: 'address' | 'email') {
    const validatorsMap = {
      address: [
        {
          field: 'membership-primary-card-address',
          equal: {
            message: this.membershipErrorMessages.missingPrimaryCardAddress.message,
            test: () => {
              if (this.isCardType(cardholder, 'primary')) {
                const isValidAddress = this.addressService.validateAddress(cardholder?.address ?? {});
                return [isValidAddress, true];
              }

              return [true, true];
            },
          },
        },
      ],
      email: [
        {
          field: 'membership-primary-card-email',
          equal: {
            message: this.membershipErrorMessages.missingPrimaryCardEmailForAutorenew.message,
            test: () => {
              if (
                this.isCardType(cardholder, 'primary') &&
                this.showAutorenewDisclaimer &&
                this.membership.membershipInfo.isAutorenew === true
              ) {
                const isValidEmail = this.ValidateService.validators.email(cardholder.email);
                return [isValidEmail, true];
              }

              return [true, true];
            },
          },
        },
      ],
    };

    const validators = validatorsMap[fieldName] || [];
    this.ValidateService.validate(validators);
    const errors = this.ValidateService.getErrorHash();

    return Object.keys(errors).length;
  }

  findContactMatchByIndex(index) {
    const customerDuplicates = this.customerDuplicates[index];
    return customerDuplicates && this.showCustomers ? customerDuplicates.match : null;
  }

  checkCardholderMatchesDupesStatus(index, status) {
    const entity = this.customerDuplicates[index];

    if (!entity) {
      return false;
    }

    return entity.dupesStatus === status;
  }

  hasMemberships(cardholder) {
    return !!this.customerMemberships.find((customerMembership) => {
      return (
        cardholder.customerId === customerMembership.id &&
        customerMembership.memberships &&
        customerMembership.memberships.length
      );
    });
  }

  getSelectedCustomerMemberships(cardholder) {
    const customerMembership = this.customerMemberships.find((customerMembership) => {
      return cardholder.customerId === customerMembership.id;
    });

    return customerMembership.memberships || [];
  }
}

export const CartMembershipComponentModule = angular
  .module('acme.view.application.cart.components.cart-membership', [
    IconComponentModule,
    VCardDirectiveModule,
    AddressServiceModule,
    'acme.common.service.membership',
    CartMembershipsServiceModule,
    'acme.common.service.validate',
    AutorenewRetailPriceComponentModule,
  ])
  .component('acmeCartMembership', {
    templateUrl: 'views/application/views/cart/components/cart-membership/cart-membership.component.tpl.html',
    controller: CartMembershipController,
    bindings: {
      membership: '<',
      membershipErrorMessages: '<',
      showCustomers: '<',
      customerDuplicates: '<',
      customerMemberships: '<',
      membershipLevelDetail: '<',
      offeringChanged: '&',
      showCardModal: '&',
      showRemoveCardModal: '&',
      showReviewPotentialMatchesModal: '&',
      restoreCardholder: '&',
      autorenewChanged: '&',
      isLoading: '<',
    },
  }).name;
